/**
 * Single source of truth: which disposal parents may enter tax totals / exports.
 * Form 8949, Disposal-CSV, PDF totals and section audits must share this gate.
 */

/** Fee-token disposals (VTHO gas / VIP-191 abstraction) — same completeness gates as other disposals. */
export const FEE_TOKEN_DISPOSAL_CLASSES = new Set([
  "gas_fee",
  "gas_abstraction_fee",
  "dex_aggregator_fee",
]);

export function isFeeTokenDisposalClass(cls) {
  return FEE_TOKEN_DISPOSAL_CLASSES.has(
    String(cls || "").trim().toLowerCase()
  );
}

/** NFT rows that represent a capital disposal in customer-facing exports. */
export const NFT_CAPITAL_DISPOSAL_CLASSES = new Set([
  "sale",
  "sale_payment",
  "nft_mint",
  "mint",
  "nft_galaxy_mint",
  "galaxy_mint",
  "nft_marketplace_purchase_payment",
  "nft_transform_disposal",
]);

export function isNftCapitalDisposalClass(cls) {
  return NFT_CAPITAL_DISPOSAL_CLASSES.has(
    String(cls || "").trim().toLowerCase()
  );
}

function firstFinite(...vals) {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * True when a row genuinely needs manual review / could not finish tax computation.
 * excludedFromTaxTotals alone is NOT a review signal (e.g. DE protocol-conversion parents).
 */
export function isActualTaxReviewRow(row = {}) {
  const reviewReason = String(
    row.reviewReason || row.taxReviewReason || ""
  ).trim();

  return Boolean(
    row.reviewRequired === true ||
      row.needsReview === true ||
      row.requiresManualReview === true ||
      row.taxComputationSkipped === true ||
      row.isTaxComputationBlocked === true ||
      row.missingCostBasis === true ||
      row.costBasisUnknown === true ||
      row.cost_basis_unknown === true ||
      row.pricingMissing === true ||
      row.priceMissing === true ||
      row.missingFiatPrice === true ||
      row.requiresUserCostBasis === true ||
      row.userCostBasisRequired === true ||
      reviewReason
  ) || statusLooksUnresolved(row);
}

function statusLooksUnresolved(row = {}) {
  const joined = [
    row.displayPriceStatus,
    row.pricingStatus,
    row.taxStatus,
    row.taxableStatus,
    row.taxResolutionStatus,
    row.acquisitionStatus,
    row.reviewReason,
    row.pricingReason,
    row.taxFamilyReviewReason,
  ]
    .map((v) => String(v || "").toLowerCase())
    .join("|");

  return (
    joined.includes("price_not_available") ||
    joined.includes("missing_price") ||
    joined.includes("token_price_not_available") ||
    joined.includes("cost_basis_unknown") ||
    joined.includes("cost_basis_not_available") ||
    joined.includes("unknown_cost_basis") ||
    joined.includes("needs_cost_basis_review") ||
    joined.includes("manual_review") ||
    joined.includes("review_required") ||
    joined.includes("liquidity_family_incomplete") ||
    joined.includes("liquidity_underlying_price_missing")
  );
}

/**
 * Multi-mint cost-basis helpers: not tax disposals and not review items.
 * Used only for later NFT sale cost matching.
 */
export function isInternalNftAcquisitionAllocationRow(row = {}) {
  if (!row || typeof row !== "object") return false;

  if (row.allocationRecordOnly === true) return true;

  const role = String(row.taxExportRole || "").toLowerCase();
  if (role === "nft_acquisition_only") return true;

  const cls = String(row.classification || row.type || "").toLowerCase();
  return cls === "nft_acquisition_allocation";
}

/**
 * @param {object} row disposal parent (not a bare fifo leg)
 * @param {{ requireCompleteMoney?: boolean }} [opts]
 */
export function isTaxExportEligibleDisposal(row = {}, opts = {}) {
  if (!row || typeof row !== "object") return false;

  if (isInternalNftAcquisitionAllocationRow(row)) {
    return false;
  }

  const explicitlyExcluded =
    row.includedInTaxTotals === false ||
    row.excludedFromTaxTotals === true ||
    row.taxComputationSkipped === true ||
    row.isTaxComputationBlocked === true ||
    row.needsReview === true ||
    row.reviewRequired === true ||
    row.requiresManualReview === true;

  const unresolvedBasis =
    row.costBasisUnknown === true ||
    row.missingCostBasis === true ||
    row.cost_basis_unknown === true ||
    row.requiresUserCostBasis === true ||
    row.userCostBasisRequired === true;

  const unresolvedPrice =
    row.pricingMissing === true ||
    row.priceMissing === true ||
    row.missingFiatPrice === true ||
    row.feePricingMissing === true;

  if (explicitlyExcluded || unresolvedBasis || unresolvedPrice) {
    return false;
  }

  if (statusLooksUnresolved(row)) {
    return false;
  }

  if (opts.requireCompleteMoney === true) {
    const grossSell = firstFinite(row.grossSellFiat, row.grossSellEUR, row.euroAmount);
    const grossBuy = firstFinite(row.grossBuyFiat, row.grossBuyEUR);
    const taxable = firstFinite(
      row.taxableProfitFiat,
      row.taxableProfitEUR,
      row.taxableProfit
    );
    if (grossSell == null || grossBuy == null || taxable == null) {
      return false;
    }
  }

  /*
   * Fee-token disposals (gas_fee / gas_abstraction_fee) use the same gate:
   * complete price + cost basis → eligible; incomplete → review.
   * Classification alone never bypasses unresolved money fields.
   */
  return true;
}

export function partitionTaxExportDisposals(allDisposals = [], opts = {}) {
  const taxableDisposals = [];
  const reviewDisposals = [];

  for (const row of Array.isArray(allDisposals) ? allDisposals : []) {
    if (isTaxExportEligibleDisposal(row, opts)) {
      taxableDisposals.push(row);
    } else {
      reviewDisposals.push(row);
    }
  }

  return { taxableDisposals, reviewDisposals };
}

/** Parent key used to detect review legs leaking into Form 8949. */
export function disposalEventKeyFromRow(row = {}, fallback = "") {
  const hash = String(
    row.hash || row.txHash || row.txid || row.disposal_hash || row.disposalHash || ""
  ).toLowerCase();

  const clause = row.clause_index ?? row.clauseIndex;
  const event = row.event_index ?? row.eventIndex;
  const log = row.log_index ?? row.logIndex;

  if (hash && clause != null && clause !== "" && event != null && event !== "") {
    let key = `${hash}#c${Number(clause)}:e${Number(event)}`;
    if (log != null && log !== "" && Number(log) !== Number(event)) {
      key += `:l${Number(log)}`;
    }
    return key;
  }

  const eventUniq = [
    row.event_index ?? row.eventIndex,
    row.log_index ?? row.logIndex,
    row.clause_index ?? row.clauseIndex,
    row.token || row.asset,
    row.classification || row.type,
  ]
    .filter((v) => v != null && v !== "")
    .join(":");

  if (hash && eventUniq) return `${hash}#${eventUniq}`;
  if (hash) return hash;
  return String(
    row.disposal_event_key ||
      row.disposalEventKey ||
      row.Event_Key ||
      fallback ||
      ""
  );
}

/**
 * Every Form 8949 line must reference an eligible parent.
 * @returns {{ ok: boolean, leakedKeys: string[], message?: string }}
 */
export function assertForm8949ParentsEligible({
  form8949Rows = [],
  taxableDisposals = [],
  reviewDisposals = [],
} = {}) {
  const taxableKeys = new Set(
    (taxableDisposals || []).map((r) => disposalEventKeyFromRow(r)).filter(Boolean)
  );
  const reviewKeys = new Set(
    (reviewDisposals || []).map((r) => disposalEventKeyFromRow(r)).filter(Boolean)
  );

  const leakedKeys = [];

  for (const line of form8949Rows || []) {
    const parentKey = String(
      line.parentDisposalEventKey ||
        line.disposal_event_key ||
        line.disposalEventKey ||
        disposalEventKeyFromRow(line) ||
        ""
    );

    if (!parentKey) {
      leakedKeys.push("(missing_parent_key)");
      continue;
    }

    if (reviewKeys.has(parentKey) || !taxableKeys.has(parentKey)) {
      // Allow lines whose parent hash matches a taxable disposal when event uniq differs
      const hashOnly = parentKey.split("#")[0];
      const taxableHashHit = [...taxableKeys].some(
        (k) => k === parentKey || k === hashOnly || k.startsWith(`${hashOnly}#`)
      );
      const reviewHit =
        reviewKeys.has(parentKey) ||
        [...reviewKeys].some(
          (k) => k === parentKey || k === hashOnly || k.startsWith(`${hashOnly}#`)
        );

      if (reviewHit || !taxableHashHit) {
        leakedKeys.push(parentKey);
      }
    }
  }

  const uniqueLeaked = [...new Set(leakedKeys)];
  if (uniqueLeaked.length > 0) {
    return {
      ok: false,
      leakedKeys: uniqueLeaked,
      message: `FORM8949_REVIEW_LEAK: ${uniqueLeaked.length} parent key(s)`,
    };
  }

  return { ok: true, leakedKeys: [] };
}

export function summarizeForm8949CsvMoney(form8949Rows = []) {
  let proceeds = 0;
  let costBasis = 0;
  let gainLoss = 0;
  let shortTerm = 0;
  let longTerm = 0;
  let legCount = 0;

  for (const row of form8949Rows || []) {
    const p = Number(row.proceeds ?? 0);
    const c = Number(row.cost_basis ?? row.costBasis ?? 0);
    const g = Number(row.gain_loss ?? row.gainLoss ?? 0);
    proceeds += Number.isFinite(p) ? p : 0;
    costBasis += Number.isFinite(c) ? c : 0;
    gainLoss += Number.isFinite(g) ? g : 0;
    legCount += 1;

    const term = String(row.term || "").toUpperCase();
    if (term === "ST" || term === "SHORT_TERM") shortTerm += Number.isFinite(g) ? g : 0;
    if (term === "LT" || term === "LONG_TERM") longTerm += Number.isFinite(g) ? g : 0;
  }

  return { proceeds, costBasis, gainLoss, shortTerm, longTerm, legCount };
}

export function summarizeDisposalCsvMoney(disposalRows = [], keys = {}) {
  const proceedsKey = keys.proceeds || "Erlös_USD";
  const feeKey =
    keys.feeCost ||
    keys.transactionCosts ||
    keys.fee ||
    "Transaktionskosten_USD";
  const costKey = keys.cost || "Kostenbasis_USD";
  const taxableKey = keys.taxable || "Steuerpflichtig_USD";
  const stKey = keys.shortTerm || "Short_Term_USD";
  const ltKey = keys.longTerm || "Long_Term_USD";

  let grossProceeds = 0;
  let feeCosts = 0;
  let costBasis = 0;
  let gainLoss = 0;
  let shortTerm = 0;
  let longTerm = 0;
  let eventCount = 0;

  for (const row of disposalRows || []) {
    grossProceeds += Number(row[proceedsKey] ?? row.grossSellFiat ?? 0) || 0;
    feeCosts +=
      Number(
        row[feeKey] ??
          row.transactionCostFiat ??
          row.feeCostFiat ??
          row.parentTransactionCostFiat ??
          0
      ) || 0;
    costBasis += Number(row[costKey] ?? row.grossBuyFiat ?? 0) || 0;
    gainLoss += Number(row[taxableKey] ?? row.taxableProfitFiat ?? 0) || 0;
    shortTerm += Number(row[stKey] ?? row.shortTermProfitFiat ?? 0) || 0;
    longTerm += Number(row[ltKey] ?? row.longTermProfitFiat ?? 0) || 0;
    eventCount += 1;
  }

  const netProceeds = grossProceeds - feeCosts;

  return {
    // Form 8949 reports proceeds net of disposal transaction costs.
    proceeds: netProceeds,
    grossProceeds,
    feeCosts,
    netProceeds,
    costBasis,
    gainLoss,
    shortTerm,
    longTerm,
    eventCount,
  };
}

const MONEY_EPS = 1e-8;

export function assertDisposalForm8949MoneyMatch(disposalMoney, form8949Money, eps = MONEY_EPS) {
  const fields = [
    ["proceeds", "Proceeds(net)"],
    ["costBasis", "CostBasis"],
    ["gainLoss", "GainLoss"],
    ["shortTerm", "ShortTerm"],
    ["longTerm", "LongTerm"],
  ];

  const mismatches = [];
  for (const [key, label] of fields) {
    const a = Number(disposalMoney?.[key] || 0);
    const b = Number(form8949Money?.[key] || 0);
    if (Math.abs(a - b) > eps) {
      mismatches.push({ field: label, disposal: a, form8949: b, delta: a - b });
    }
  }

  if (mismatches.length) {
    const err = new Error(
      `FORM8949_DISPOSAL_MISMATCH: ${mismatches
        .map((m) => `${m.field} delta=${m.delta}`)
        .join("; ")}`
    );
    err.mismatches = mismatches;
    throw err;
  }

  return true;
}

/**
 * Event-aware Form 8949 ↔ Disposal reconciliation.
 * Legs are grouped under normalized parent disposal keys; money is compared net.
 */
export function reconcileDisposalForm8949({
  disposalRows = [],
  form8949Rows = [],
  keys = {},
  eps = MONEY_EPS,
} = {}) {
  const disposalMoney = summarizeDisposalCsvMoney(disposalRows, keys);
  const formMoney = summarizeForm8949CsvMoney(form8949Rows);

  const mismatches = [];
  try {
    assertDisposalForm8949MoneyMatch(disposalMoney, formMoney, eps);
  } catch (err) {
    for (const m of err?.mismatches || []) mismatches.push(m);
  }

  // Row-count difference alone is never a failure (lot vs event).
  const ok = mismatches.length === 0;

  return {
    ok,
    status: ok ? "verified" : "mismatch",
    disposalMoney,
    formMoney,
    mismatches,
    disposalEventCount: disposalMoney.eventCount || 0,
    form8949LegCount: formMoney.legCount || 0,
    message: ok
      ? `Form 8949 reconciliation complete. ${formMoney.legCount || 0} FIFO lot line(s) reconcile to ${disposalMoney.eventCount || 0} disposal event(s). Proceeds are reported net of disposal transaction costs.`
      : `Form 8949 reconciliation failed: ${mismatches
          .map((m) => `${m.field} Δ=${Number(m.delta).toFixed(8)}`)
          .join("; ")}`,
  };
}
